import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { Formik, Form } from 'formik';
import moment from 'moment/moment';
import lodashFlatten from 'lodash/fp/flatten';
import lodashOmit from 'lodash/fp/omit';
import lodashOmitBy from 'lodash/fp/omitBy';
import lodashCompact from 'lodash/fp/compact';
import lodashIsUndefined from 'lodash/fp/isUndefined';
import emptyFunction from 'common/util/emptyFunction';
import { stateOptionItems } from 'common/util/stateCodes';

import addressStreetSuffixes from 'common/addressStreetSuffixes';
import capitalize from 'common/util/capitalize';
import setNullEmptyValues from 'common/util/setNullEmptyValues';

import {
  MARKETING_PARTNER_LISTING_STATUS_OPTION_ITEMS,
  MARKETING_PARTNER_LISTING_STATUS_TEMPORARILY_OFF_MARKET,
  MARKETING_PARTNER_LISTING_STATUS_WITHDRAWN,
  MARKETING_PARTNER_LISTING_TEMPORARILY_OFF_MARKET_REASON_OPTION_ITEMS,
  MARKETING_PARTNER_LISTING_WITHDRAWN_REASON_OPTION_ITEMS,
  MARKETING_PARTNER_LISTING_TEMPORARILY_OFF_MARKET_REASON_OTHER,
  MARKETING_PARTNER_LISTING_WITHDRAWN_REASON_OTHER,
  PROPERTY_SETUP_ACCESS_TYPE_OPTION_ITEMS,
  PROPERTY_SETUP_ACCESS_TYPE_CONTACT_FOR_KEY,
  PROPERTY_SETUP_ACCESS_TYPE_COMBO_LOCKBOX,
  PROPERTY_SETUP_ACCESS_TYPE_HIDDEN,
  PROPERTY_SETUP_ACCESS_TYPE_SUPRA_LOCKBOX,
  PROPERTY_SETUP_ACCESS_METHOD_OPTION_ITEMS,
  PROPERTY_SETUP_ACCESS_METHOD_TYPE_KEY,
  PROPERTY_SETUP_ACCESS_METHOD_TYPE_CODE,
  MARKETING_PARTNER_LISTING_CATEGORY_PRE_LEASE,
  MARKETING_PARTNER_LISTING_CATEGORY_MOVE_IN_READY,
} from 'common/marketingPartnerListingConstants';

import { additionalDetailsFields } from 'common/listingFormFields';

import {
  SR_API_ACTION_GET_LISTING_BY_FUZZY_ADDRESS_SEARCH,
  SR_API_ACTION_GET_APARTMENT_BUILDINGS_BY_FUZZY_ADDRESS_SEARCH,
  SR_API_ACTION_PARTNER_CREATE_MARKETING_LISTING,
  SR_API_ACTION_PARTNER_UPDATE_MARKETING_LISTING,
  SR_API_ACTION_TENANT_SCREENING_CRITERIA_UPSERT,
  SR_API_ACTION_GET_LISTING_BY_APARTMENT_UNIT_SEARCH,
  SR_API_ACTION_GET_PARTNER_APARTMENTS_LIST,
  SR_API_ACTION_UPDATE_BREED_RESTRICTIONS,
  SR_API_ACTION_FUZZY_SEARCH,
  SR_API_ACTION_GOOGLE_MAPS_GET_AUTOCOMPLETE_PREDICTIONS,
  SR_API_ACTION_GOOGLE_MAPS_GET_PLACE_DETAILS,
  SR_API_ACTION_PARTNER_END_PRE_LEASE,
} from 'api/actionTypes';

import {
  LISTING_OCCUPANT_TYPES,
  LISTING_OCCUPANT_TYPE_VACANT,
  LISTING_DATA_SOURCE_SUNROOM,
  LISTING_PRELEASE_TIME_WINDOW_IN_DAYS,
  LISTING_LOCKBOX_LOCATIONS,
} from 'common/listingConstants';

import { PARTNER_TYPE_AGENT } from 'common/partnerConstants';

import {
  PROPERTY_TYPE_OPTIONS_PARTNER,
  PROPERTY_TYPE_OPTION_VALUE_APARTMENT,
} from 'common/propertyTypes';

import {
  getListingFullAddressString,
  makeListingAddressStreetDisplay,
} from 'common/listingHelpers';

import { cleanMarketingPartnerListingFormValues } from 'common/marketingPartnerListingHelpers';

import { hasRoleAdmin, hasRolePropertyManager } from 'common/userRoles';

import momentWithAustinTimezone from 'common/util/momentWithAustinTimezone';

import {
  scrollToFormField,
  createInitialValues,
  createFormValidationFn,
  hideFieldIf,
  showFieldIf,
} from 'client/helpers/formikHelpers';

import petWeightOptionItems from 'client/helpers/petWeightOptionItems';

import { extractListingAddressFieldsFromGooglePlace } from 'client/lib/googlePlaces';

import { petFieldsToNullify } from 'server/models/Listing/fieldgroups';

import ButtonDeprecated from 'client/components/ButtonDeprecated';
import SrDarkModal from 'client/components/SrDarkModal';
import SrModalContent from 'client/components/SrModalContent';
import SrMarketPropertyPhotos from 'client/components/SrMarketPropertyPhotos/SrMarketPropertyPhotos';
import SrFormikField, { SR_FIELD_THEME_PARTNER_FORM } from 'client/components/SrFormikField';
import SrButtonDeprecated from 'client/components/SrButtonDeprecated';
import SrBox from 'client/components/SrBox';
import SrText from 'client/components/SrText';
import SrEditListingTenantScreeningCriteriaCta from 'client/components/SrEditListingTenantScreeningCriteriaCta';
import SrTextList from 'client/components/SrTextList';
import { SR_TOOLTIP_POSITION_CENTER_UNDERNEATH } from 'client/components/SrTooltip';

import postWithState from 'client/actions/postWithState';
import withApiState from 'client/helpers/withApiState';
import branch from 'client/core/branch';
import log from 'client/lib/log';

export const FORM_TYPE_MARKET_LISTING = 'FORM_TYPE_MARKET_LISTING';
export const FORM_TYPE_UPDATE_PRELEASING_LISTING = 'FORM_TYPE_UPDATE_PRELEASING_LISTING';
export const FORM_TYPE_COMPLETE_PRELEASING_LISTING = 'FORM_TYPE_COMPLETE_PRELEASING_LISTING';
const FORM_TYPES = [FORM_TYPE_MARKET_LISTING, FORM_TYPE_UPDATE_PRELEASING_LISTING];

function getDefaultFormValues({ marketingPartnerListing, propertyAttributes, formType }) {
  const defaultFormValues = marketingPartnerListing
    ? {
        ...marketingPartnerListing,
        address: getListingFullAddressString(marketingPartnerListing.listing),
        addressStreetNumber: marketingPartnerListing.listing.addressStreetNumber,
        addressZip: marketingPartnerListing.listing.addressZip,
        apartmentUnit: marketingPartnerListing.listing.apartmentUnit,
        landlordName: marketingPartnerListing.landlordName,
        landlordEmail: marketingPartnerListing.landlordEmail,
        landlordPhone: marketingPartnerListing.landlordPhone,
        accountManagerUserId: marketingPartnerListing.listing.accountManagerUserId,
        photoUrls: marketingPartnerListing.listing.photoUrls,
      }
    : {
        isPreleasingListing: false,
        ownerListingActiveAlert: false,
        ...propertyAttributes.reduce(
          (memo, propertyAttribute) => ({
            ...memo,
            [propertyAttribute.name]: propertyAttribute.attrs.value,
          }),
          {},
        ),
      };

  if (marketingPartnerListing?.preLease) {
    defaultFormValues.partnerHasExteriorPhoto = !marketingPartnerListing.preLease
      .requireExteriorPhoto;

    // Going to market card or update pre-lease listing
    if (!formType || formType === FORM_TYPE_UPDATE_PRELEASING_LISTING) {
      defaultFormValues.isPreleasingListing = true;
    }
  }

  return defaultFormValues;
}

function getFormFields({
  isNew,
  isAgent,
  values = {},
  googleAddressMatches = [],
  addressMatches = [],
  onFindListings,
  onAddressChange,
  onGoogleAddressChange,
  apartmentNameMatches,
  apartmentUnitMatches,
  onFindApartmentUnits,
  onFindGoogleListings,
  onApartmentUnitChange,
  requestApartmentNameChoices,
  requireAutoCompleteAddress,
  landlordSuggestionChoices = [],
  currentUser,
  partner,
  partnerUsers,
  formType,
  makeLandlordSuggestionsGetterByFieldFunc = emptyFunction,
  onLandlordChange = emptyFunction,
  onPreLeasePropertyChange = emptyFunction,
  onAccessMethodChange,
  onPropertySetupAccessTypeChange,
}) {
  const isAdmin = hasRoleAdmin(currentUser.roles);
  const shouldShowMissingApartmentUnitError =
    !(apartmentUnitMatches || []).find(
      ({ apartmentUnit }) => apartmentUnit === values.apartmentUnit,
    ) &&
    values.apartmentUnit &&
    (apartmentUnitMatches || []).length <= 1;

  const shouldShowPetFields = values.dogsAllowed || values.catsAllowed;

  const shouldShowListingIdError =
    !shouldShowMissingApartmentUnitError &&
    requireAutoCompleteAddress &&
    (!(requireAutoCompleteAddress && values.apartmentBuildingId) || values.apartmentUnit);

  const shouldShowOccupantFields =
    values.occupantType && values.occupantType !== LISTING_OCCUPANT_TYPE_VACANT;

  // eslint-disable-next-line max-len
  const shouldShowKeyContact =
    values.accessMethod === PROPERTY_SETUP_ACCESS_METHOD_TYPE_KEY &&
    values.propertySetupAccessType === PROPERTY_SETUP_ACCESS_TYPE_CONTACT_FOR_KEY;

  const shouldShowTemporarilyOffMarketFields =
    values.status === MARKETING_PARTNER_LISTING_STATUS_TEMPORARILY_OFF_MARKET;
  const shouldShowWithdrawalReasonField =
    values.status === MARKETING_PARTNER_LISTING_STATUS_WITHDRAWN;

  const requireSetupDate =
    values.status !== MARKETING_PARTNER_LISTING_STATUS_TEMPORARILY_OFF_MARKET &&
    values.status !== MARKETING_PARTNER_LISTING_STATUS_WITHDRAWN;

  const propertyManagers = partnerUsers?.filter(({ user }) => hasRolePropertyManager(user.roles));
  const currentPhotos = values.currentPhotos || [];
  const shouldMarketPreleasing =
    formType === FORM_TYPE_MARKET_LISTING && partner.experienceConfiguration.preleasingEnabled;

  const isUpdatePreleasingForm = formType === FORM_TYPE_UPDATE_PRELEASING_LISTING;
  const isCompletePreleasingForm = formType === FORM_TYPE_COMPLETE_PRELEASING_LISTING;
  const isAutoPropertyConditionReportEnabled = !!partner.experienceConfiguration
    .automaticPropertyConditionReports; // eslint-disable-line max-len
  const shouldHidePropertyConditionReport = isCompletePreleasingForm
    ? false
    : !isNew ||
      !partner.propertyConditionReportEnabled ||
      values.isPreleasingListing ||
      isAutoPropertyConditionReportEnabled;

  const formFields = lodashFlatten(
    lodashCompact([
      showFieldIf(shouldMarketPreleasing, {
        id: 'isPreleasingListing',
        title: 'pre-lease this property?',
        tooltipCopy:
          'To activate pre-leasing, at least one photo is required. ' +
          'you can upload below or we can take one for you.',
        tooltipPosition: SR_TOOLTIP_POSITION_CENTER_UNDERNEATH,
        tooltipBgColor: 'blueBright',
        tooltipClassName: 'tooltip-market-property-form-preleasing',
        subtitle:
          'your listing will be live before the property is ready for a tour. ' +
          "renters will be able to apply and we'll notify them when the home is ready to tour.",
        properties: {
          onChange: onPreLeasePropertyChange,
        },
        type: 'yes_no',
        validations: ['required'],
      }),
      showFieldIf(
        partner.experienceConfiguration.listingPropertyManagerEnabled &&
          propertyManagers?.length > 0,
        {
          id: 'propertyManagerUserId',
          title: 'property manager',
          type: 'select',
          properties: {
            placeholder: 'Choose property manager',
            enableResetOption: true,
            transformToNumber: true,
            choices: propertyManagers?.map(({ user }) => ({
              value: user.id,
              label: user.name,
            })),
          },
          validations: ['required'],
        },
      ),
      {
        id: 'listingId',
        type: 'hidden',
        validations: shouldShowListingIdError ? ['requiredAddressNotFound'] : null,
      },
      {
        id: 'apartmentBuildingId',
        type: 'hidden',
      },
      !isNew &&
        hideFieldIf(
          values.isPreleasingListing || isUpdatePreleasingForm || isCompletePreleasingForm,
          {
            id: 'status',
            title: 'status',
            type: 'select',
            properties: {
              choices: MARKETING_PARTNER_LISTING_STATUS_OPTION_ITEMS.filter((option) => {
                return !(
                  option.value === MARKETING_PARTNER_LISTING_STATUS_WITHDRAWN &&
                  !isAdmin &&
                  values.status !== MARKETING_PARTNER_LISTING_STATUS_WITHDRAWN
                );
              }),
              disabled: !isAdmin && values.status === MARKETING_PARTNER_LISTING_STATUS_WITHDRAWN,
            },
            validations: ['required'],
          },
        ),
      hideFieldIf(
        !shouldShowTemporarilyOffMarketFields ||
          values.isPreleasingListing ||
          isUpdatePreleasingForm,
        {
          id: 'temporarilyOffMarketReason',
          title: 'reason for taking temp. off-market?',
          label: (
            <>
              <div
                style={{
                  color: '#E7711B',
                  fontWeight: '600',
                  marginTop: '-5px',
                  marginBottom: '7px',
                }}
              >
                (the owner will see this message)
              </div>
            </>
          ),
          type: 'select',
          properties: {
            choices: MARKETING_PARTNER_LISTING_TEMPORARILY_OFF_MARKET_REASON_OPTION_ITEMS,
          },
          validations: ['required'],
        },
      ),
      hideFieldIf(
        !(
          shouldShowTemporarilyOffMarketFields &&
          values.temporarilyOffMarketReason ===
            MARKETING_PARTNER_LISTING_TEMPORARILY_OFF_MARKET_REASON_OTHER
        ) ||
          values.isPreleasingListing ||
          isUpdatePreleasingForm,
        {
          id: 'temporarilyOffMarketReasonProvided',
          title: 'other reason for taking temp. off-market?',
          label: (
            <>
              <div
                style={{
                  color: '#E7711B',
                  fontWeight: '600',
                  marginTop: '-5px',
                  marginBottom: '7px',
                }}
              >
                (the owner will see this message)
              </div>
            </>
          ),
          type: 'long_text',
          properties: {
            placeholder: 'Write here...',
          },
          validations: ['required'],
        },
      ),
      hideFieldIf(!shouldShowTemporarilyOffMarketFields || isUpdatePreleasingForm, {
        id: 'bringBackToMarketDate',
        title: 'bring back on market date (optional)',
        type: 'date_picker',
        properties: {
          placeholderValue: 'MM/DD/YYYY',
          placeholder: 'MM/DD/YYYY',
          dateFormat: 'MM/DD/YYYY',
        },
      }),
      hideFieldIf(!shouldShowWithdrawalReasonField || isUpdatePreleasingForm, {
        id: 'withdrawalReason',
        title: 'reason for withdrawing?',
        type: 'select',
        properties: {
          choices: MARKETING_PARTNER_LISTING_WITHDRAWN_REASON_OPTION_ITEMS,
        },
        validations: ['required'],
      }),
      hideFieldIf(
        !(
          shouldShowWithdrawalReasonField &&
          values.withdrawalReason === MARKETING_PARTNER_LISTING_WITHDRAWN_REASON_OTHER
        ),
        {
          id: 'withdrawalReasonProvided',
          title: 'other reason for withdrawing',
          type: 'long_text',
          properties: {
            placeholder: 'Write here...',
          },
          validations: ['required'],
        },
      ),
      hideFieldIf(!requireAutoCompleteAddress, {
        id: 'address',
        title: 'address',
        label: '(must choose dropdown option)',
        type: 'autocomplete',
        validations: ['required'],
        properties: {
          placeholder: '',
          choices: addressMatches.map((listing) => listing.addressStreetDisplay),
          onSuggestionsFetchRequested: onFindListings,
          onChange: onAddressChange,
          disabled: !isNew,
          filterFunc: () => {
            return true;
          },
        },
      }),
      hideFieldIf(
        requireAutoCompleteAddress || isUpdatePreleasingForm || isCompletePreleasingForm,
        {
          id: 'addressStreetNumber',
          title: 'street number',
          type: 'short_text',
          validations: ['required'],
          properties: {
            placeholder: '',
            disabled: !isNew,
          },
        },
      ),
      hideFieldIf(
        requireAutoCompleteAddress || isUpdatePreleasingForm || isCompletePreleasingForm,
        {
          id: 'addressStreetName',
          title: 'street name',
          type: 'autocomplete',
          validations: ['required'],
          properties: {
            choices: googleAddressMatches.map(({ description }) => description),
            onSuggestionsFetchRequested: ({ value }) => {
              return onFindGoogleListings({ ...values, addressStreetName: value });
            },
            onChange: onGoogleAddressChange,
            disabled: !isNew,
            filterFunc: () => {
              return true;
            },
          },
        },
      ),
      {
        id: 'addressStreetSuffix',
        type: 'hidden',
      },
      !requireAutoCompleteAddress &&
        hideFieldIf(isUpdatePreleasingForm || isCompletePreleasingForm, {
          id: 'apartmentUnit',
          title: 'unit',
          label: '(optional)',
          type: 'short_text',
          properties: {
            disabled: !isNew,
          },
        }),
      hideFieldIf(
        requireAutoCompleteAddress || isUpdatePreleasingForm || isCompletePreleasingForm,
        {
          id: 'addressZip',
          title: 'zip code',
          type: 'short_text',
          validations: ['required'],
          properties: {
            placeholder: '',
            disabled: !isNew,
          },
        },
      ),
      hideFieldIf(
        requireAutoCompleteAddress || isUpdatePreleasingForm || isCompletePreleasingForm,
        {
          id: 'addressCity',
          title: 'city',
          type: 'short_text',
          validations: ['required'],
          properties: {
            placeholder: '',
            disabled: !isNew,
          },
        },
      ),
      hideFieldIf(
        requireAutoCompleteAddress || isUpdatePreleasingForm || isCompletePreleasingForm,
        {
          id: 'addressStateCode',
          title: 'state',
          type: 'select',
          properties: {
            choices: stateOptionItems,
            disabled: !isNew,
          },
          validations: ['required'],
        },
      ),
      requireAutoCompleteAddress &&
        values.apartmentBuildingId &&
        hideFieldIf(isUpdatePreleasingForm || isCompletePreleasingForm, {
          id: 'apartmentUnit',
          title: 'unit',
          label: '(must choose dropdown option)',
          type: 'autocomplete',
          validations: lodashCompact([
            'required',
            shouldShowMissingApartmentUnitError && 'addressNotFound',
          ]),
          properties: {
            placeholder: '',
            choices: (apartmentUnitMatches || []).map(({ apartmentUnit }) => apartmentUnit),
            onSuggestionsFetchRequested: ({ value }) =>
              onFindApartmentUnits({ apartmentUnit: value, values }),
            onChange: onApartmentUnitChange,
            disabled: !isNew,
            filterFunc: ({ value, choice }) => {
              const lowerCaseChoice = choice.toLowerCase();
              return lowerCaseChoice.includes(value);
            },
          },
        }),
      hideFieldIf(isUpdatePreleasingForm || isCompletePreleasingForm, {
        id: 'propertyType',
        title: 'property type?',
        type: 'select',
        properties: {
          choices: PROPERTY_TYPE_OPTIONS_PARTNER.map((listingType) => ({
            label: listingType,
            value: listingType,
          })),
        },
        validations: ['required'],
      }),
      hideFieldIf(
        values.propertyType !== PROPERTY_TYPE_OPTION_VALUE_APARTMENT || isUpdatePreleasingForm,
        {
          id: 'apartmentName',
          title: 'apartment complex name?',
          type: 'autocomplete',
          properties: {
            choices: apartmentNameMatches || [],
            onSuggestionsFetchRequested: requestApartmentNameChoices || emptyFunction,
          },
          validations: ['required'],
        },
      ),
      hideFieldIf(isUpdatePreleasingForm, {
        id: 'bedrooms',
        title: 'beds',
        type: 'number',
        validations: ['required'],
      }),
      hideFieldIf(isUpdatePreleasingForm, {
        id: 'bathrooms',
        title: 'baths',
        type: 'number',
        validations: ['required'],
      }),
      hideFieldIf(isUpdatePreleasingForm, {
        id: 'squareFeet',
        title: 'square feet',
        type: 'numberWithComma',
        validations: ['required'],
      }),
      hideFieldIf(isUpdatePreleasingForm, {
        id: 'price',
        title: 'rent price',
        type: 'currency',
        validations: ['required'],
      }),
      hideFieldIf(isUpdatePreleasingForm, {
        id: 'securityDeposit',
        title: 'security deposit',
        type: 'currency',
        validations: ['required'],
      }),
      showFieldIf(
        partner.experienceConfiguration.underwrittenRentEnabled && !isUpdatePreleasingForm,
        {
          id: 'underwrittenRent',
          title: 'underwritten rent',
          type: 'currency',
          validations: ['required'],
        },
      ),
      hideFieldIf(
        isCompletePreleasingForm || isUpdatePreleasingForm,
        additionalDetailsFields.map((formField) => ({
          ...formField,
          title: formField.title.toLowerCase(),
        })),
      ),
      showFieldIf(values.isPreleasingListing || isUpdatePreleasingForm, {
        id: 'preleaseStartDate',
        title: 'week available for tour?',
        type: 'week_picker',
        validations: ['required', 'noPastDates'],
        properties: {
          placeholderValue: 'Select starting date',
          placeholder: 'Select starting date',
          dateFormat: 'MM/DD/YYYY',
          readOnly: true,
          isValidDate: (date) => date.isAfter(momentWithAustinTimezone().subtract(1, 'days')),
        },
      }),
      showFieldIf(values.isPreleasingListing && !isUpdatePreleasingForm, {
        id: 'partnerHasExteriorPhoto',
        title:
          'do you have a photo of the listing to use for pre-marketing? ' +
          'if not, sunroom will get one',
        subtitle: 'note: once tour-able, sunroom can return and take full interior/exterior photos',
        type: 'yes_no',
        validations: ['required'],
      }),
      showFieldIf(
        (values.isPreleasingListing &&
          typeof values.partnerHasExteriorPhoto === 'boolean' &&
          values.partnerHasExteriorPhoto) ||
          isUpdatePreleasingForm,
        {
          id: 'photoUrls',
          title: isUpdatePreleasingForm ? 'current photos' : null,
          type: 'file_multi',
          properties: {
            filePath: '/listing-uploads/photos/preleasing/{uuid}',
            fileAccept: 'image/*, .heic',
            placeholder: 'Tap to upload',
            value: values.photoUrls,
            replaceFields: !isUpdatePreleasingForm,
            hidePreview: !isUpdatePreleasingForm,
            maxFiles: 40,
          },
          validations: isUpdatePreleasingForm ? [] : ['required', 'nonEmptyArray'],
        },
      ),
      hideFieldIf(values.isPreleasingListing || isUpdatePreleasingForm, {
        id: 'accessMethod',
        title: 'property access method',
        type: 'select',
        properties: {
          choices: PROPERTY_SETUP_ACCESS_METHOD_OPTION_ITEMS,
          onChange: onAccessMethodChange,
        },
        validations: ['required'],
      }),
      hideFieldIf(
        values.accessMethod !== PROPERTY_SETUP_ACCESS_METHOD_TYPE_CODE ||
          values.isPreleasingListing ||
          isUpdatePreleasingForm,
        {
          id: 'propertyAccessCode',
          title: 'what is the access code?',
          type: 'long_text',
          properties: {
            placeholder: '1234#',
          },
          validations: ['required'],
        },
      ),
      hideFieldIf(
        values.accessMethod !== PROPERTY_SETUP_ACCESS_METHOD_TYPE_KEY ||
          values.isPreleasingListing ||
          isUpdatePreleasingForm,
        {
          id: 'propertySetupAccessType',
          title: 'key access',
          type: 'select',
          properties: {
            choices: PROPERTY_SETUP_ACCESS_TYPE_OPTION_ITEMS,
            onChange: onPropertySetupAccessTypeChange,
          },
          validations: ['required'],
        },
      ),
      hideFieldIf(
        values.accessMethod !== PROPERTY_SETUP_ACCESS_METHOD_TYPE_KEY ||
          values.propertySetupAccessType !== PROPERTY_SETUP_ACCESS_TYPE_HIDDEN ||
          values.isPreleasingListing ||
          isUpdatePreleasingForm,
        {
          id: 'keyPickupDescription',
          title: 'where is the key located?',
          type: 'long_text',
          properties: {
            placeholder: 'The key is located...',
          },
          validations: ['required'],
        },
      ),
      hideFieldIf(
        values.accessMethod !== PROPERTY_SETUP_ACCESS_METHOD_TYPE_KEY ||
          values.propertySetupAccessType !== PROPERTY_SETUP_ACCESS_TYPE_HIDDEN ||
          values.isPreleasingListing ||
          isUpdatePreleasingForm,
        {
          id: 'keyPickupPhoto',
          type: 'file_single',
          properties: {
            fileAccept: '*',
            filePath: '/listing-uploads/key-pickup-photos/{uuid}',
          },
          validations: [],
        },
      ),
      hideFieldIf(
        values.accessMethod !== PROPERTY_SETUP_ACCESS_METHOD_TYPE_KEY ||
          values.propertySetupAccessType !== PROPERTY_SETUP_ACCESS_TYPE_COMBO_LOCKBOX ||
          values.isPreleasingListing ||
          isUpdatePreleasingForm,
        {
          id: 'lockboxCode',
          title: 'combo lockbox code',
          type: 'short_text',
          properties: {
            placeholder: 'Write lockbox code here...',
          },
          validations: ['required'],
        },
      ),
      hideFieldIf(
        values.accessMethod !== PROPERTY_SETUP_ACCESS_METHOD_TYPE_KEY ||
          values.propertySetupAccessType !== PROPERTY_SETUP_ACCESS_TYPE_COMBO_LOCKBOX ||
          values.isPreleasingListing ||
          isUpdatePreleasingForm,
        {
          id: 'lockboxLocation',
          title: 'combo lockbox location',
          type: 'select',
          properties: {
            enableResetOption: true,
            choices: LISTING_LOCKBOX_LOCATIONS.map((lockboxLocation) => ({
              label: lockboxLocation,
              value: lockboxLocation,
            })),
          },
          validations: ['required'],
        },
      ),
      hideFieldIf(
        values.accessMethod !== PROPERTY_SETUP_ACCESS_METHOD_TYPE_KEY ||
          values.propertySetupAccessType !== PROPERTY_SETUP_ACCESS_TYPE_SUPRA_LOCKBOX ||
          values.isPreleasingListing ||
          isUpdatePreleasingForm,
        {
          id: 'supraCbsCode',
          title: 'supra CBS code',
          type: 'short_text',
          properties: {
            placeholder: '1234567',
            pattern: '[0-9]*',
          },
          validations: ['required', 'supraCbs'],
        },
      ),
      hideFieldIf(
        values.accessMethod !== PROPERTY_SETUP_ACCESS_METHOD_TYPE_KEY ||
          values.propertySetupAccessType === PROPERTY_SETUP_ACCESS_TYPE_CONTACT_FOR_KEY ||
          !values.propertySetupAccessType ||
          values.isPreleasingListing ||
          isUpdatePreleasingForm,
        {
          id: 'dateKeyExpectedAtListing',
          title: 'date the key will be at the listing',
          subtitle:
            'if the key is not at the listing on the date indicated, the listing setup may be delayed.',
          type: 'date_picker',
          properties: {
            placeholder: 'MM/DD/YYYY',
          },
          validations: ['required'],
        },
      ),
      hideFieldIf(!shouldShowKeyContact || values.isPreleasingListing || isUpdatePreleasingForm, {
        id: 'propertyAccessContactName',
        title: 'key contact name',
        type: 'short_text',
        validations: ['required'],
      }),
      hideFieldIf(!shouldShowKeyContact || values.isPreleasingListing || isUpdatePreleasingForm, {
        id: 'propertyAccessContactPhone',
        title: 'key contact phone',
        type: 'short_text',
        validations: ['required', 'phone'],
      }),
      hideFieldIf(!shouldShowKeyContact || values.isPreleasingListing || isUpdatePreleasingForm, {
        id: 'propertyAccessContactEmail',
        title: 'key contact email',
        type: 'short_text',
        validations: ['required', 'email'],
      }),
      hideFieldIf(values.isPreleasingListing || isUpdatePreleasingForm, {
        id: 'availableForSetupDate',
        title: 'date available for setup?',
        type: 'date_picker',
        validations: lodashCompact([
          requireSetupDate && 'required',
          'noPastDates',
          'afterOrSameThanDate',
        ]),
        properties: {
          placeholderValue: 'MM/DD/YYYY',
          placeholder: 'MM/DD/YYYY',
          dateFormat: 'MM/DD/YYYY',
          readOnly: false,
          isValidDate: (date) => date.isAfter(momentWithAustinTimezone().subtract(1, 'days')),
          date: values.dateKeyExpectedAtListing,
          afterThanDateMessage: 'Date cannot be before the key available date',
        },
      }),
      hideFieldIf(values.isPreleasingListing || isUpdatePreleasingForm, {
        id: 'availableDate',
        title: 'when can a renter move in?',
        type: 'date_picker',
        validations: ['required', 'afterOrSameThanDate'],
        properties: {
          placeholderValue: 'MM/DD/YYYY',
          placeholder: 'MM/DD/YYYY',
          dateFormat: 'MM/DD/YYYY',
          readOnly: false,
          date: values.availableForSetupDate,
          afterThanDateMessage: 'Date cannot be before available for setup date',
        },
      }),
      hideFieldIf(values.isPreleasingListing || isUpdatePreleasingForm, {
        id: 'yardSignAllowed',
        title: 'yard sign allowed?',
        type: 'yes_no',
        validations: ['required'],
      }),
      hideFieldIf(values.isPreleasingListing || isUpdatePreleasingForm, {
        id: 'requireSunlock',
        title: 'install sunlock?',
        subtitle: '(only installed once the property is vacant)',
        type: 'yes_no',
        validations: ['required'],
      }),
      {
        id: 'noRequireSunlockReason',
        title: values.requireSunlock === false ? 'reason for not installing a sunlock?' : '',
        type: values.requireSunlock === false ? 'long_text' : 'hidden',
        validations: values.requireSunlock === false ? ['required'] : [],
        properties: {
          placeholder: '',
        },
      },
      hideFieldIf(values.isPreleasingListing || isUpdatePreleasingForm, {
        id: 'requirePhotos',
        title: currentPhotos.length > 0 ? 're-take the photos?' : 'photos needed?',
        type: 'yes_no',
        validations: ['required'],
      }),
      hideFieldIf(currentPhotos.length > 0 || values.requirePhotos !== false, {
        id: 'linkToExistingPhotos',
        title: 'link to the photos',
        label: '(dropbox, zillow, etc.)',
        type: 'short_text',
        properties: {
          placeholder: 'Paste the link to your photos...',
        },
        validations: ['required'],
      }),
      hideFieldIf(values.isPreleasingListing || isUpdatePreleasingForm, {
        id: 'requireVirtual3dTour',
        title: '3d tour needed?',
        label: '(additional fees)',
        type: 'yes_no',
        validations: ['required'],
      }),
      hideFieldIf(shouldHidePropertyConditionReport, {
        id: 'requirePropertyConditionReport',
        title: 'do you want sunroom to inspect the property and provide a condition report?',
        type: 'yes_no',
        validations: ['required'],
      }),
      hideFieldIf(isCompletePreleasingForm || isUpdatePreleasingForm, {
        id: 'dogsAllowed',
        title: 'dogs allowed?',
        type: 'yes_no',
        validations: ['required'],
      }),
      hideFieldIf(isCompletePreleasingForm || isUpdatePreleasingForm, {
        id: 'catsAllowed',
        title: 'cats allowed?',
        type: 'yes_no',
        validations: ['required'],
      }),
      hideFieldIf(isCompletePreleasingForm || isUpdatePreleasingForm, {
        id: 'petFee',
        title: shouldShowPetFields ? 'pet fee' : '',
        type: shouldShowPetFields ? 'currency' : 'hidden',
        validations: shouldShowPetFields ? ['required'] : [],
      }),
      hideFieldIf(isCompletePreleasingForm || isUpdatePreleasingForm, {
        id: 'petFeePerPet',
        title: shouldShowPetFields ? 'pet fee structure' : '',
        type: shouldShowPetFields ? 'yes_no' : 'hidden',
        properties: {
          labels: {
            yes: 'Per Pet',
            no: 'Covers All',
          },
        },
        validations: shouldShowPetFields ? ['required'] : [],
      }),
      hideFieldIf(
        isCompletePreleasingForm ||
          isUpdatePreleasingForm ||
          !shouldShowPetFields ||
          !values.petFeePerPet,
        {
          id: 'additionalPetFee',
          title: 'fee per additional pet',
          validations: ['required'],
          type: 'currency',
        },
      ),
      hideFieldIf(isCompletePreleasingForm || isUpdatePreleasingForm, {
        id: 'petDeposit',
        title: shouldShowPetFields ? 'pet deposit' : '',
        type: shouldShowPetFields ? 'currency' : 'hidden',
        validations: shouldShowPetFields ? ['required'] : [],
      }),
      hideFieldIf(isCompletePreleasingForm || isUpdatePreleasingForm, {
        id: 'petDepositPerPet',
        title: shouldShowPetFields ? 'pet deposit structure' : '',
        type: shouldShowPetFields ? 'yes_no' : 'hidden',
        properties: {
          labels: {
            yes: 'Per Pet',
            no: 'Covers All',
          },
        },
        validations: shouldShowPetFields ? ['required'] : [],
      }),
      hideFieldIf(
        isCompletePreleasingForm ||
          isUpdatePreleasingForm ||
          !shouldShowPetFields ||
          !values.petDepositPerPet,
        {
          id: 'additionalPetDeposit',
          title: 'deposit per additional pet',
          validations: ['required'],
          type: 'currency',
        },
      ),
      hideFieldIf(isCompletePreleasingForm || isUpdatePreleasingForm, {
        id: 'monthlyPetRent',
        title: shouldShowPetFields ? 'monthly pet rent' : '',
        type: shouldShowPetFields ? 'currency' : 'hidden',
        validations: shouldShowPetFields ? ['required'] : [],
      }),
      hideFieldIf(isCompletePreleasingForm || isUpdatePreleasingForm, {
        id: 'monthlyPetRentPerPet',
        title: shouldShowPetFields ? 'pet rent structure' : '',
        type: shouldShowPetFields ? 'yes_no' : 'hidden',
        properties: {
          labels: {
            yes: 'Per Pet',
            no: 'Covers All',
          },
        },
        validations: shouldShowPetFields ? ['required'] : [],
      }),
      hideFieldIf(
        isCompletePreleasingForm ||
          isUpdatePreleasingForm ||
          !shouldShowPetFields ||
          !values.monthlyPetRentPerPet,
        {
          id: 'additionalMonthlyPetRent',
          title: 'monthly pet rent per additional pet',
          validations: ['required'],
          type: 'currency',
        },
      ),
      hideFieldIf(isCompletePreleasingForm || isUpdatePreleasingForm, {
        id: 'maximumPetsAllowed',
        title: shouldShowPetFields ? 'max pets' : '',
        type: shouldShowPetFields ? 'number' : 'hidden',
        validations: shouldShowPetFields ? ['positiveNumberString'] : [],
      }),
      hideFieldIf(isCompletePreleasingForm || isUpdatePreleasingForm, {
        id: 'petWeight',
        title: shouldShowPetFields ? 'max pet weight' : '',
        type: shouldShowPetFields ? 'select' : 'hidden',
        properties: shouldShowPetFields
          ? {
              placeholder: 'Choose maximum pet weight',
              enableResetOption: true,
              choices: petWeightOptionItems.map((petWeight) => ({
                label: petWeight.label,
                value: petWeight.value,
              })),
              transformToNumber: true,
            }
          : {},
        validations: shouldShowPetFields ? ['required'] : [],
      }),
      hideFieldIf(isUpdatePreleasingForm || isCompletePreleasingForm, {
        id: 'occupantType',
        title: 'occupant type',
        type: 'select',
        properties: {
          placeholder: 'Choose occupant type',
          enableResetOption: true,
          choices: LISTING_OCCUPANT_TYPES.map((listingType) => ({
            label: listingType,
            value: listingType,
          })),
        },
        validations: ['required'],
      }),
      hideFieldIf(!shouldShowOccupantFields, {
        id: 'vacantDate',
        title: 'when will this property become vacant?',
        type: 'date_picker',
        validations: ['required'],
        properties: {
          placeholderValue: 'MM/DD/YYYY',
          placeholder: 'MM/DD/YYYY',
          dateFormat: 'MM/DD/YYYY',
          readOnly: false,
        },
      }),
      {
        id: 'occupantName',
        title: shouldShowOccupantFields ? 'occupant name' : '',
        type: shouldShowOccupantFields ? 'short_text' : 'hidden',
        validations: shouldShowOccupantFields ? ['required'] : [],
      },
      {
        id: 'occupantPhone',
        title: shouldShowOccupantFields ? 'occupant phone' : '',
        type: shouldShowOccupantFields ? 'phone' : 'hidden',
        validations: shouldShowOccupantFields ? ['required', 'phone'] : [],
      },
      {
        id: 'occupantEmail',
        title: shouldShowOccupantFields ? 'occupant email' : '',
        type: shouldShowOccupantFields ? 'email' : 'hidden',
        validations: shouldShowOccupantFields ? ['email'] : [],
      },
      hideFieldIf(isUpdatePreleasingForm, {
        id: 'gateCode',
        title: 'gate code (optional)',
        type: 'short_text',
        properties: {
          placeholder: 'Write gate code here...',
        },
      }),
      showFieldIf(!isUpdatePreleasingForm, {
        id: 'hasAlarmCode',
        title: 'is there an alarm?',
        type: 'yes_no',
        validations: ['required'],
      }),
      showFieldIf(!isUpdatePreleasingForm && values.hasAlarmCode, {
        id: 'alarmCode',
        title: 'alarm code',
        type: 'short_text',
        validations: ['required'],
        properties: {
          placeholder: 'Write alarm code here...',
        },
      }),
      showFieldIf(!isUpdatePreleasingForm && values.hasAlarmCode, {
        id: 'alarmTurnOffInstructions',
        title: 'instructions for renter to use alarm code',
        type: 'long_text',
        validations: ['required'],
        properties: {
          placeholder: 'Write instructions here…',
        },
      }),
      showFieldIf(!isUpdatePreleasingForm && values.hasAlarmCode, {
        id: 'alarmTurnOnInstructions',
        title: 'instructions for renter to enable the alarm when they leave',
        subtitle: "(sunroom can't guarantee renter will enable the alarm)",
        type: 'long_text',
        validations: ['required'],
        properties: {
          placeholder: 'Write instructions here…',
        },
      }),
      hideFieldIf(values.isPreleasingListing || isUpdatePreleasingForm, {
        id: 'showingInstructions',
        title: 'special showing instructions (optional)',
        type: 'long_text',
        properties: {
          placeholder: 'Write instructions here…',
        },
      }),
      hideFieldIf(isUpdatePreleasingForm, {
        id: 'partnerMarketingNotes',
        title: 'extra notes (optional)',
        type: 'long_text',
        properties: {
          placeholder: 'Write notes here…',
        },
      }),
      hideFieldIf(!isAgent, {
        id: 'landlordName',
        title: 'landlord name',
        type: 'autocomplete',
        properties: {
          choices: landlordSuggestionChoices,
          onSuggestionsFetchRequested: makeLandlordSuggestionsGetterByFieldFunc('name'),
          onChange: onLandlordChange,
          filterFunc: ({ value, choice }) => {
            const lowerCaseChoice = choice.toLowerCase();
            return lowerCaseChoice.includes(value);
          },
        },
        validations: ['required'],
      }),
      hideFieldIf(!isAgent, {
        id: 'landlordEmail',
        title: 'landlord email',
        subtitle: '(for notifications on listing activity)',
        type: 'autocomplete',
        properties: {
          choices: landlordSuggestionChoices,
          onSuggestionsFetchRequested: makeLandlordSuggestionsGetterByFieldFunc('email'),
          onChange: onLandlordChange,
          filterFunc: ({ value, choice }) => {
            const lowerCaseChoice = choice.toLowerCase();
            return lowerCaseChoice.includes(value);
          },
        },
        validations: ['required'],
      }),
      hideFieldIf(!isAgent, {
        id: 'landlordPhone',
        title: 'landlord phone',
        subtitle: '(for notifications on listing activity)',
        type: 'autocomplete',
        properties: {
          choices: landlordSuggestionChoices,
          onSuggestionsFetchRequested: makeLandlordSuggestionsGetterByFieldFunc('phone'),
          onChange: onLandlordChange,
          filterFunc: ({ value, choice }) => {
            const lowerCaseChoice = choice.toLowerCase();
            return lowerCaseChoice.includes(value);
          },
        },
        validations: ['required', 'phone'],
      }),
      hideFieldIf(isAgent || isUpdatePreleasingForm || isCompletePreleasingForm, {
        id: 'ownerListingActiveAlert',
        type: 'checkbox',
        properties: {
          className: 'theme-squareCheckbox ownerListingActiveAlert',
        },
      }),
      hideFieldIf(!values.ownerListingActiveAlert, {
        id: 'ownerEmail',
        title: 'owner email',
        type: 'short_text',
        validations: ['email', 'required'],
      }),
      (isNew || isCompletePreleasingForm) && {
        id: 'confirmUnitIsClean',
        title: '',
        validations: ['legal'],
        type: 'checkbox',
        properties: {
          className: 'theme-squareCheckbox confirmUnitIsClean',
        },
      },
    ]),
  );

  return formFields;
}

function makeLandlordMatchString({ name, phone, email }) {
  return `${name} - ${phone} - ${email}`;
}

@withApiState({
  createMarketingPartnerListingApiState: {
    action: SR_API_ACTION_PARTNER_CREATE_MARKETING_LISTING,
  },
  updateMarketingPartnerListingApiState: {
    action: SR_API_ACTION_PARTNER_UPDATE_MARKETING_LISTING,
  },
})
@branch((props) => ({
  partnerUsers: ['partners', 'users', 'listByPartnerId', props.partner.id],
}))
class SrPartnerMarketListingModal extends Component {
  static propTypes = {
    partner: PropTypes.object.isRequired,
    propertyAttributes: PropTypes.array,
    dispatch: PropTypes.func.isRequired,
    createMarketingPartnerListingApiState: PropTypes.object.isRequired,
    updateMarketingPartnerListingApiState: PropTypes.object.isRequired,
    reloadDataTableMarketingPartnerListing: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    isNew: PropTypes.bool.isRequired,
    marketingPartnerListing: PropTypes.object,
    currentUser: PropTypes.object.isRequired,
    partnerUsers: PropTypes.array,
    formType: PropTypes.oneOf(FORM_TYPES),
  };

  constructor(props) {
    super(props);

    this.state = {
      addressMatches: [],
      apartmentUnitMatches: [],
      landlordMatches: [],
      formError: null,
      apartmentNameMatches: null,
      lastApartmentUnitSearch: null,
      tenantScreeningCriteria: null,
    };

    this._listingMatch = null;
  }

  componentWillUnmount() {
    this._isUnmounting = true;
  }

  _onFindGoogleListings = (props) => {
    const { dispatch } = this.props;
    if (!props.addressStreetName || !props.addressStreetNumber) {
      this.setState({
        googleAddressMatches: [],
      });
      return;
    }

    const searchInput = `${props.addressStreetNumber} ${props.addressStreetName}`;
    dispatch(postWithState, {
      queryId: searchInput,
      action: SR_API_ACTION_GOOGLE_MAPS_GET_AUTOCOMPLETE_PREDICTIONS,
      payload: {
        input: searchInput,
        limitToTexas: false,
      },
    }).then((autocompletePredictions) => {
      this.setState({
        googleAddressMatches: autocompletePredictions,
      });
    });
  };

  _onGoogleAddressChange = ({ newValue, formikBag }) => {
    const { googleAddressMatches } = this.state;
    const selectedMatch = googleAddressMatches?.find(({ description }) => description === newValue);
    if (selectedMatch?.place_id) {
      this._loadMatchAddressIntoForm({
        placeId: selectedMatch.place_id,
        formikBag,
      });
    }
  };

  _loadMatchAddressIntoForm = ({ placeId, formikBag }) => {
    const { dispatch } = this.props;
    dispatch(postWithState, {
      action: SR_API_ACTION_GOOGLE_MAPS_GET_PLACE_DETAILS,
      payload: {
        placeId,
      },
    }).then((googlePlace) => {
      const listingMatch = extractListingAddressFieldsFromGooglePlace(googlePlace);
      if (listingMatch) {
        formikBag.setFieldValue('addressStreetNumber', listingMatch.addressStreetNumber);
        formikBag.setFieldValue('addressStreetName', listingMatch.addressStreetName);
        formikBag.setFieldValue('addressCity', listingMatch.addressCity);
        formikBag.setFieldValue('addressStateCode', listingMatch.addressState);
        formikBag.setFieldValue('addressZip', listingMatch.addressZip);
      }
    });
  };

  _onFindListingByListingAddress = ({ value }) => {
    const { dispatch, partner } = this.props;

    const allResults = [];

    dispatch(postWithState, {
      action: SR_API_ACTION_GET_LISTING_BY_FUZZY_ADDRESS_SEARCH,
      queryId: `${value}`,
      payload: {
        addressStreetDisplay: value,
        listingManagementCompany: partner.managementCompanyName,
        excludeApartmentUnits: true,
        dataSource: LISTING_DATA_SOURCE_SUNROOM,
        limit: 8,
      },
    })
      .then((listings) => {
        allResults.push(...listings);
        return dispatch(postWithState, {
          action: SR_API_ACTION_GET_APARTMENT_BUILDINGS_BY_FUZZY_ADDRESS_SEARCH,
          queryId: `${value}`,
          payload: {
            partnerId: partner.id,
            addressStreetDisplay: value,
          },
        });
      })
      .then((apartmentBuildings) => {
        allResults.push(
          ...apartmentBuildings.map((apartmentBuilding) => ({
            listingId: null,
            apartmentBuildingId: apartmentBuilding.id,
            addressStreetDisplay: apartmentBuilding.addressStreetDisplay,
          })),
        );
        if (!this._isUnmounting) {
          this.setState({ addressMatches: allResults });
        }
      })
      .catch(log.error);
  };

  _onFindApartmentUnitsByUnitNumber = (formikBag) => {
    return ({ apartmentUnit, values }) => {
      const { dispatch } = this.props;
      this.setState({
        lastApartmentUnitSearch: apartmentUnit,
      });

      dispatch(postWithState, {
        action: SR_API_ACTION_GET_LISTING_BY_APARTMENT_UNIT_SEARCH,
        queryId: `${values.apartmentBuildingId}-${apartmentUnit}`,
        payload: {
          apartmentBuildingId: values.apartmentBuildingId,
          apartmentUnit: apartmentUnit,
          limit: 8,
        },
      })
        .then((apartmentUnitMatches) => {
          const { lastApartmentUnitSearch } = this.state;
          if (!this._isUnmounting && lastApartmentUnitSearch === apartmentUnit) {
            this.setState({ apartmentUnitMatches: apartmentUnitMatches || [] });
            if (apartmentUnit) {
              const apartmentUnitMatch = (apartmentUnitMatches || []).find((match) => {
                return apartmentUnit === match.apartmentUnit;
              });
              if (apartmentUnitMatch) {
                return this._setListingChangeFormikBagValues(apartmentUnitMatch, formikBag);
              }
            }
          }
        })
        .catch(log.error);
    };
  };

  _onRequestApartmentNameChoices = ({ value }) => {
    const { partner, dispatch } = this.props;

    dispatch(postWithState, {
      action: SR_API_ACTION_GET_PARTNER_APARTMENTS_LIST,
      payload: {
        partnerId: partner.id,
        apartmentNameFuzzy: value,
      },
    })
      .then((apartmentNameMatches) => {
        if (!this._isUnmounting) {
          this.setState({
            apartmentNameMatches: (apartmentNameMatches || []).map(({ name }) => name),
          });
        }
      })
      .catch(log.error);
  };

  _makeLandlordSuggestionsGetterByFieldFunc = (fieldId) => {
    return ({ value }) => {
      const { dispatch } = this.props;
      dispatch(postWithState, {
        action: SR_API_ACTION_FUZZY_SEARCH,
        queryId: `${fieldId}-${value}`,
        payload: {
          tableName: 'landlords',
          fieldId: fieldId,
          searchQuery: value,
        },
      })
        .then((landlordMatches) => {
          if (!this._isUnmounting && landlordMatches) {
            this.setState({
              landlordMatches,
            });
          }
        })
        .catch(log.error);
    };
  };

  _onValidateForm = (formikBag) => {
    const { partner, partnerUsers, isNew, currentUser, formType } = this.props;
    const { apartmentUnitMatches } = this.state;

    const formValues = formikBag.values;
    const formFields = getFormFields({
      isNew: isNew,
      isAgent: partner.type === PARTNER_TYPE_AGENT,
      values: formValues,
      requireAutoCompleteAddress: partner.marketPropertyRequireExistingListingFlag,
      apartmentUnitMatches: apartmentUnitMatches,
      currentUser: currentUser,
      partner,
      partnerUsers,
      formType,
    });
    const formErrors = createFormValidationFn(formFields)(formValues);
    const formFielIdsWithErrors = Object.keys(formErrors);

    if (!formFielIdsWithErrors.length) {
      formikBag.handleSubmit();
    } else {
      formFielIdsWithErrors.forEach((formFieldId) => {
        formikBag.setFieldTouched(formFieldId, true, true);
      });
      scrollToFormField({
        formFieldId: formFielIdsWithErrors[0],
      });
    }
  };

  _onSubmitForm = (values) => {
    const {
      isNew,
      partner,
      marketingPartnerListing,
      reloadDataTableMarketingPartnerListing,
      onClose,
      dispatch,
      formType,
    } = this.props;
    const { tenantScreeningCriteria } = this.state;

    const fieldsToRemove = ['confirmUnitIsClean', 'address', 'apartmentBuildingId'];

    const updates = setNullEmptyValues(
      lodashOmit(fieldsToRemove, lodashOmitBy(lodashIsUndefined, values)),
    );
    updates.listingId = updates.listingId || null;
    updates.partnerId = partner.id;
    updates.petsAllowed = updates.dogsAllowed || updates.catsAllowed;

    if (!partner.marketPropertyRequireExistingListingFlag && isNew) {
      updates.addressStreetDisplay = makeListingAddressStreetDisplay(updates);
    }

    if (!updates.petsAllowed) {
      for (const petFieldId of petFieldsToNullify) {
        if (petFieldId in updates) {
          updates[petFieldId] = null;
        }
      }
    }

    if (updates.addressStreetName) {
      const splitAddress = updates.addressStreetName.split(' ');
      const capitalizedLastWord = capitalize(splitAddress[splitAddress.length - 1].toLowerCase());
      const includesSuffix = addressStreetSuffixes.includes(capitalizedLastWord);
      if (includesSuffix) {
        updates.addressStreetSuffix = capitalizedLastWord;
        updates.addressStreetName = splitAddress.slice(0, -1).join(' ');
      }
    }

    if (updates.occupantType === LISTING_OCCUPANT_TYPE_VACANT) {
      updates.occupantName = null;
      updates.occupantPhone = null;
      updates.occupantEmail = null;
    }

    if (updates.requireSunlock) {
      updates.noRequireSunlockReason = null;
      updates.sunlockAllowed = true;
    } else {
      updates.sunlockAllowed = false;
    }

    if (updates.photoUrls === null) {
      delete updates.photoUrls;
    }

    if (formType === FORM_TYPE_COMPLETE_PRELEASING_LISTING) {
      updates.isPreleaseCompletion = true;
    }

    if (formType === FORM_TYPE_MARKET_LISTING && updates.isPreleasingListing) {
      updates.exteriorPhotoRequired = !updates.partnerHasExteriorPhoto;
    }

    if (isNew) {
      updates.category = updates.isPreleasingListing
        ? MARKETING_PARTNER_LISTING_CATEGORY_PRE_LEASE
        : MARKETING_PARTNER_LISTING_CATEGORY_MOVE_IN_READY;
    }

    const resetAccessValues = ({
      propertyAccessCode = null,
      keyPickupDescription = null,
      keyPickupPhoto = null,
      supraCbsCode = null,
      propertyAccessContactName = null,
      propertyAccessContactPhone = null,
      propertyAccessContactEmail = null,
      lockboxCode = null,
    }) => {
      // Access Code
      updates.propertyAccessCode = propertyAccessCode;
      // HiddenKey
      updates.keyPickupDescription = keyPickupDescription;
      updates.keyPickupPhoto = keyPickupPhoto;
      // SupraLockbox
      updates.supraCbsCode = supraCbsCode;
      // ContactForKey
      updates.propertyAccessContactName = propertyAccessContactName;
      updates.propertyAccessContactPhone = propertyAccessContactPhone;
      updates.propertyAccessContactEmail = propertyAccessContactEmail;
      // CombinationLockbox
      updates.lockboxCode = lockboxCode;
    };

    if (updates.accessMethod === PROPERTY_SETUP_ACCESS_METHOD_TYPE_CODE) {
      resetAccessValues({
        propertyAccessCode: updates.propertyAccessCode,
      });
    } else if (updates.accessMethod === PROPERTY_SETUP_ACCESS_METHOD_TYPE_KEY) {
      switch (updates.propertySetupAccessType) {
        case PROPERTY_SETUP_ACCESS_TYPE_COMBO_LOCKBOX:
          resetAccessValues({
            lockboxCode: updates.lockboxCode,
          });
          break;
        case PROPERTY_SETUP_ACCESS_TYPE_CONTACT_FOR_KEY:
          resetAccessValues({
            propertyAccessContactName: updates.propertyAccessContactName,
            propertyAccessContactPhone: updates.propertyAccessContactPhone,
            propertyAccessContactEmail: updates.propertyAccessContactEmail,
          });
          break;
        case PROPERTY_SETUP_ACCESS_TYPE_HIDDEN:
          resetAccessValues({
            keyPickupDescription: updates.keyPickupDescription,
            keyPickupPhoto: updates.keyPickupPhoto,
          });
          break;
        case PROPERTY_SETUP_ACCESS_TYPE_SUPRA_LOCKBOX:
          resetAccessValues({
            supraCbsCode: updates.supraCbsCode,
          });
          break;
        default:
          break;
      }
    }

    dispatch(postWithState, {
      action:
        formType === FORM_TYPE_COMPLETE_PRELEASING_LISTING
          ? SR_API_ACTION_PARTNER_END_PRE_LEASE
          : isNew
          ? SR_API_ACTION_PARTNER_CREATE_MARKETING_LISTING
          : SR_API_ACTION_PARTNER_UPDATE_MARKETING_LISTING,
      payload: {
        updates: cleanMarketingPartnerListingFormValues(updates),
        marketingPartnerListingId: isNew ? null : marketingPartnerListing.id,
      },
      onSuccess: (marketingPartnerListing) => {
        onClose();
        if (tenantScreeningCriteria) {
          dispatch(postWithState, {
            action: SR_API_ACTION_TENANT_SCREENING_CRITERIA_UPSERT,
            payload: {
              listingId: marketingPartnerListing.listingId,
              tenantScreeningCriteria: lodashOmit(
                ['id', 'breedRestrictions'],
                setNullEmptyValues(tenantScreeningCriteria),
              ),
            },
          });
          if (tenantScreeningCriteria.breedRestrictions?.length > 0) {
            dispatch(postWithState, {
              action: SR_API_ACTION_UPDATE_BREED_RESTRICTIONS,
              payload: {
                listingId: marketingPartnerListing.listingId,
                breedRestrictions: tenantScreeningCriteria.breedRestrictions,
              },
            });
          }
        }

        return reloadDataTableMarketingPartnerListing(marketingPartnerListing);
      },
      onError: (error) => {
        this.setState({
          formError: error.message,
        });
        log.error(error);
      },
    });
  };

  _setListingChangeFormikBagValues = (match, formikBag) => {
    formikBag.setFieldValue('listingId', match.id || null);
    formikBag.setFieldValue('apartmentBuildingId', match.apartmentBuildingId || null);
    formikBag.setFieldValue('currentPhotos', match.sunroomPhotoUrls || match.floorPlanPhotos || []);
    if (match.id) {
      this._listingMatch = match;
      formikBag.setFieldValue('address', getListingFullAddressString(match));
      formikBag.setFieldValue('petFee', match.petFee);
      formikBag.setFieldValue('petDeposit', match.petDeposit);
      formikBag.setFieldValue('monthlyPetRent', match.monthlyPetRent);
      formikBag.setFieldValue('maximumPetsAllowed', match.maximumPetsAllowed);
      formikBag.setFieldValue('petWeight', match.petWeight);
      formikBag.setFieldValue('addressStreetName', match.addressStreetName);
      formikBag.setFieldValue('addressStreetNumber', match.addressStreetNumber);
      formikBag.setFieldValue('addressStreetSuffix', match.addressStreetSuffix);
      formikBag.setFieldValue('addressZip', match.addressZip);
      formikBag.setFieldValue('addressCity', match.addressCity);
      formikBag.setFieldValue('addressStateCode', match.addressStateCode);
      formikBag.setFieldValue('addressStreetDisplay', match.addressStreetDisplay);
      formikBag.setFieldValue('apartmentUnit', match.apartmentUnit);
      formikBag.setFieldValue('bathrooms', match.bathrooms);
      formikBag.setFieldValue('bedrooms', match.bedrooms);
      formikBag.setFieldValue('squareFeet', match.squareFeet);

      if (match.apartmentName) {
        formikBag.setFieldValue('apartmentName', match.apartmentName);
      }

      const additionalDetailsFieldIds = additionalDetailsFields.map(({ id }) => id);
      for (const formFieldId of additionalDetailsFieldIds) {
        formikBag.setFieldValue(formFieldId, match[formFieldId]);
      }
    }
  };

  _onSubmitEditListingTenantScreeningCriteria = (tenantScreeningCriteria) => {
    this.setState({
      tenantScreeningCriteria,
    });
  };

  _getTitle = () => {
    const { formType } = this.props;

    if (!formType) {
      return 'Market Property';
    }

    switch (formType) {
      case FORM_TYPE_UPDATE_PRELEASING_LISTING:
        return 'Update Preleasing';
      case FORM_TYPE_COMPLETE_PRELEASING_LISTING:
        return 'Enable Property for Tours';
      case FORM_TYPE_MARKET_LISTING:
      default:
        return 'Market Property';
    }
  };

  _getSubtitle = () => {
    const { formType } = this.props;

    if (!formType) {
      return 'You can always change property info later';
    }

    switch (formType) {
      case FORM_TYPE_UPDATE_PRELEASING_LISTING:
        return null;
      case FORM_TYPE_COMPLETE_PRELEASING_LISTING:
        return 'Pre-leasing will end and tours will be enabled once Sunroom sets the property';
      case FORM_TYPE_MARKET_LISTING:
      default:
        return 'You can always change property info later';
    }
  };

  _getSubmitButtonText = () => {
    const { formType, isNew } = this.props;

    if (isNew) {
      return 'start marketing';
    }

    if (formType === FORM_TYPE_COMPLETE_PRELEASING_LISTING) {
      return 'order tour setup';
    }

    return 'update property';
  };

  render() {
    const {
      addressMatches,
      googleAddressMatches,
      apartmentNameMatches,
      apartmentUnitMatches,
      landlordMatches,
      tenantScreeningCriteria,
      formError,
    } = this.state;
    const {
      partner,
      propertyAttributes = [],
      createMarketingPartnerListingApiState,
      updateMarketingPartnerListingApiState,
      onClose,
      marketingPartnerListing,
      isNew,
      currentUser,
      partnerUsers,
      formType,
    } = this.props;

    const isAgent = partner.type === PARTNER_TYPE_AGENT;

    const defaultValues = getDefaultFormValues({
      marketingPartnerListing,
      propertyAttributes,
      formType,
    });

    const requireAutoCompleteAddress = partner.marketPropertyRequireExistingListingFlag;
    const title = this._getTitle();
    const subtitle = this._getSubtitle();
    const submitButtonText = this._getSubmitButtonText();
    const isUpdatePreleasingForm = formType === FORM_TYPE_UPDATE_PRELEASING_LISTING;

    return (
      <SrDarkModal className="SrPartnerMarketListingModal">
        <SrModalContent onCloseIconClick={onClose}>
          <Formik
            validate={(values) => {
              return createFormValidationFn(
                getFormFields({
                  isNew,
                  isAgent,
                  values,
                  requireAutoCompleteAddress,
                  apartmentUnitMatches,
                  currentUser,
                  partner,
                  partnerUsers,
                  formType,
                }),
              )(values);
            }}
            initialValues={createInitialValues({
              formFields: getFormFields({
                isNew,
                isAgent,
                requireAutoCompleteAddress,
                values: defaultValues,
                currentUser: currentUser,
                partner: partner,
                partnerUsers: partnerUsers,
                formType,
              }),
              defaultValues: defaultValues,
            })}
            onSubmit={this._onSubmitForm}
            render={(formikBag) => {
              const formFields = getFormFields({
                isNew,
                isAgent,
                requireAutoCompleteAddress,
                addressMatches,
                googleAddressMatches,
                apartmentNameMatches,
                apartmentUnitMatches,
                values: formikBag.values,
                currentUser: currentUser,
                partner: partner,
                partnerUsers: partnerUsers,
                formType,
                onFindGoogleListings: this._onFindGoogleListings,
                onFindListings: this._onFindListingByListingAddress,
                onFindApartmentUnits: this._onFindApartmentUnitsByUnitNumber(formikBag),
                requestApartmentNameChoices: this._onRequestApartmentNameChoices,
                onGoogleAddressChange: this._onGoogleAddressChange,
                onAddressChange: ({ newValue, formikBag }) => {
                  const addressMatch = addressMatches.find(
                    ({ addressStreetDisplay }) => addressStreetDisplay === newValue,
                  );
                  if (addressMatch) {
                    this._setListingChangeFormikBagValues(addressMatch, formikBag);
                  } else {
                    formikBag.setFieldValue('listingId', '');
                    formikBag.setFieldValue('apartmentBuildingId', '');
                    formikBag.setFieldValue('currentPhotos', []);
                    formikBag.setFieldValue('petFee', '');
                    formikBag.setFieldValue('petDeposit', '');
                    formikBag.setFieldValue('monthlyPetRent', '');
                    formikBag.setFieldValue('maximumPetsAllowed', '');
                    formikBag.setFieldValue('petWeight', '');
                    const additionalDetailsFieldIds = additionalDetailsFields.map(({ id }) => id);

                    for (const formFieldId of additionalDetailsFieldIds) {
                      formikBag.setFieldValue(formFieldId, null);
                    }
                  }
                },
                onApartmentUnitChange: ({ newValue, formikBag }) => {
                  const apartmentUnitMatch = apartmentUnitMatches.find(
                    ({ apartmentUnit }) => apartmentUnit === newValue,
                  );

                  if (apartmentUnitMatch) {
                    this._setListingChangeFormikBagValues(apartmentUnitMatch, formikBag);
                  } else {
                    const addressMatch = addressMatches.find(({ apartmentBuildingId }) => {
                      return apartmentBuildingId === formikBag.values.apartmentBuildingId;
                    });
                    formikBag.setFieldValue(
                      'address',
                      addressMatch ? getListingFullAddressString(addressMatch) : '',
                    );
                    formikBag.setFieldValue('listingId', '');
                    formikBag.setFieldValue('currentPhotos', []);
                    formikBag.setFieldValue('petFee', '');
                    formikBag.setFieldValue('petDeposit', '');
                    formikBag.setFieldValue('monthlyPetRent', '');
                    formikBag.setFieldValue('maximumPetsAllowed', '');
                    formikBag.setFieldValue('petWeight', '');

                    const additionalDetailsFieldIds = additionalDetailsFields.map(({ id }) => id);

                    for (const formFieldId of additionalDetailsFieldIds) {
                      formikBag.setFieldValue(formFieldId, null);
                    }
                  }
                },
                landlordSuggestionChoices: landlordMatches.map(makeLandlordMatchString),
                makeLandlordSuggestionsGetterByFieldFunc: this
                  ._makeLandlordSuggestionsGetterByFieldFunc,
                onLandlordChange: ({ newValue, formikBag }) => {
                  const landlordMatch = landlordMatches.find((landlord) => {
                    return newValue === makeLandlordMatchString(landlord);
                  });
                  if (landlordMatch) {
                    formikBag.setFieldValue('landlordName', landlordMatch.name);
                    formikBag.setFieldValue('landlordPhone', landlordMatch.phone);
                    formikBag.setFieldValue('landlordEmail', landlordMatch.email);
                  }
                },
                onAccessMethodChange: (e) => {
                  const accessMethod = e.target.value;
                  formikBag.setFieldValue('accessMethod', accessMethod);

                  formikBag.setFieldValue('propertySetupAccessType', '');

                  if (accessMethod === PROPERTY_SETUP_ACCESS_METHOD_TYPE_CODE) {
                    formikBag.setFieldValue('dateKeyExpectedAtListing', '');
                  }
                },
                onPropertySetupAccessTypeChange: (e) => {
                  const propertySetupAccessType = e.target.value;
                  formikBag.setFieldValue('propertySetupAccessType', propertySetupAccessType);

                  if (propertySetupAccessType === PROPERTY_SETUP_ACCESS_TYPE_CONTACT_FOR_KEY) {
                    formikBag.setFieldValue('dateKeyExpectedAtListing', '');
                  }
                },
                onPreLeasePropertyChange: (isPreLeasingListing) => {
                  formikBag.resetForm();
                  formikBag.setFieldValue('isPreleasingListing', isPreLeasingListing);
                },
              });

              return (
                <>
                  <Form className="form">
                    <div className="container">
                      <div className="header">
                        <span className="title">{title}</span>
                        <span className="subtitle">{subtitle}</span>
                      </div>
                      <div className="content">
                        {formFields.map((formField) => (
                          <Fragment key={formField.id}>
                            {formField.id === 'requirePhotos' &&
                              !formikBag.values.isPreleasingListing &&
                              formType !== FORM_TYPE_UPDATE_PRELEASING_LISTING && (
                                <div className={SR_FIELD_THEME_PARTNER_FORM}>
                                  <span className="title">current photos</span>
                                  <SrMarketPropertyPhotos
                                    photos={formikBag.values.currentPhotos || []}
                                    address={formikBag.values.address || ''}
                                  />
                                </div>
                              )}
                            {formField.id === 'landlordName' && !isUpdatePreleasingForm && (
                              <SrBox marginTop={16} marginBottom={26}>
                                {(marketingPartnerListing?.listing ||
                                  !requireAutoCompleteAddress ||
                                  this._listingMatch) && (
                                  <SrEditListingTenantScreeningCriteriaCta
                                    listing={marketingPartnerListing?.listing || this._listingMatch}
                                    partner={partner}
                                    listingTenantScreeningCriteria={
                                      (!marketingPartnerListing?.listing &&
                                        !requireAutoCompleteAddress &&
                                        tenantScreeningCriteria) ||
                                      null
                                    }
                                    onSubmit={this._onSubmitEditListingTenantScreeningCriteria}
                                    omitApiActions={
                                      !marketingPartnerListing?.listing &&
                                      !requireAutoCompleteAddress
                                    }
                                  >
                                    <SrButtonDeprecated
                                      color="blue300"
                                      bgColor="grey200"
                                      borderRadius={5}
                                      text="Edit tenant qualifying criteria"
                                      leftIconClassName="pen-blue"
                                      onClick={emptyFunction}
                                      className="editPropertyDetailsButton"
                                    />
                                  </SrEditListingTenantScreeningCriteriaCta>
                                )}
                              </SrBox>
                            )}
                            {formField.id === 'confirmUnitIsClean' &&
                              formType === FORM_TYPE_COMPLETE_PRELEASING_LISTING && (
                                <SrBox>
                                  <SrText
                                    fontSize="18px"
                                    lineHeight="24px"
                                    font="whitney-sc"
                                    weight="bold"
                                  >
                                    what happens next?
                                  </SrText>
                                  <SrBox>
                                    <SrText fontSize="16px" weight="bold" font="greycliff">
                                      <SrTextList
                                        fontSize="16px"
                                        font="greycliff"
                                        weight="bold"
                                        textList={[
                                          'Sunroom will visit the property to set it up for tours',
                                          'All leads will be notified when the property ' +
                                            'is available for touring.',
                                        ]}
                                        ordered={false}
                                      />
                                      The Pre-Lease stage will officially end once the property is
                                      set up and ready for tours.
                                    </SrText>
                                  </SrBox>
                                </SrBox>
                              )}
                            <SrFormikField
                              formField={formField}
                              formikBag={formikBag}
                              className={SR_FIELD_THEME_PARTNER_FORM}
                            />
                            {formField.id === 'photoUrls' &&
                              (formikBag.values.isPreleasingListing ||
                                formType === FORM_TYPE_UPDATE_PRELEASING_LISTING) &&
                              typeof formikBag.values.partnerHasExteriorPhoto === 'boolean' &&
                              formikBag.values.partnerHasExteriorPhoto &&
                              formikBag.values.photoUrls.length === 0 && (
                                <div className={SR_FIELD_THEME_PARTNER_FORM}>
                                  <SrText
                                    fontSize="15px"
                                    lineHeight="20px"
                                    font="greycliff"
                                    color="orange300"
                                  >
                                    At least one photo is required to Pre-Lease the listing.
                                  </SrText>
                                </div>
                              )}
                            {formField.id === 'preleaseStartDate' &&
                              (formikBag.values.isPreleasingListing ||
                                formType === FORM_TYPE_UPDATE_PRELEASING_LISTING) &&
                              formikBag.values.preleaseStartDate && (
                                <div className={SR_FIELD_THEME_PARTNER_FORM}>
                                  <SrBox marginTop="-8px">
                                    <SrText fontSize="15px" lineHeight="24px" font="whitney-sc">
                                      tours will be expected to be ready in this range:
                                    </SrText>
                                    <SrText fontSize="14px" lineHeight="24px" weight="600">
                                      {` ${moment(formikBag.values.preleaseStartDate).format(
                                        'MM/DD/YYYY',
                                      )}` +
                                        ' - ' +
                                        `${moment(formikBag.values.preleaseStartDate)
                                          .add(LISTING_PRELEASE_TIME_WINDOW_IN_DAYS, 'days')
                                          .format('MM/DD/YYYY')}`}
                                    </SrText>
                                  </SrBox>
                                  <SrBox>
                                    <SrText fontSize="15px" lineHeight="24px" font="whitney-sc">
                                      this can also be updated later.
                                    </SrText>
                                  </SrBox>
                                </div>
                              )}
                          </Fragment>
                        ))}
                      </div>
                    </div>
                  </Form>
                  <div className="footer">
                    <ButtonDeprecated
                      onClick={() => this._onValidateForm(formikBag)}
                      disabled={
                        createMarketingPartnerListingApiState.inProgress ||
                        updateMarketingPartnerListingApiState.inProgress
                      }
                    >
                      {submitButtonText}
                    </ButtonDeprecated>
                    {formError && (
                      <div className="formMessageError">
                        <span>{formError}</span>
                      </div>
                    )}
                  </div>
                </>
              );
            }}
          />
        </SrModalContent>
      </SrDarkModal>
    );
  }
}

SrPartnerMarketListingModal.displayName = 'SrPartnerMarketListingModal';

export default SrPartnerMarketListingModal;
